import React from 'react';
import LegalLayout, { H2, H3, P, Li, Value, LegalNote } from '../components/LegalLayout';
import { LEGAL } from '../data/legalConstants';

const ReportHandling = () => (
  <LegalLayout title="Report Handling Policy" eyebrow="Safety Operations" lastUpdated="October 21, 2026">
    <LegalNote title="Reports are a safety system, not decoration" tone="safety">
      ROMIND KIDS treats reports as a core child-safety workflow. Reports help identify harmful content, unsafe contact, bullying, grooming signals, rule violations, and technical abuse.
    </LegalNote>

    <H2>1. Purpose</H2>
    <P>This Report Handling Policy explains how ROMIND KIDS receives, prioritizes, reviews, escalates, resolves, records, and responds to reports about content, profiles, messages, clubs, challenges, accounts, and platform behavior.</P>

    <H2>2. How Reports Can Be Submitted</H2>
    <P>Reports may be submitted through:</P>
    <ul>
      <Li>In-app report buttons on posts, comments, messages, profiles, clubs, challenges, or other content.</Li>
      <Li>Blocking flows that include a safety reason.</Li>
      <Li>Parent or legal guardian email to <Value><a href={`mailto:${LEGAL.safetyEmail}`} className="text-cyan-300 hover:text-cyan-200 transition-colors">{LEGAL.safetyEmail}</a></Value>.</Li>
      <Li>Support contact forms or designated safety forms where available.</Li>
      <Li>Moderator or RONI-generated safety escalations.</Li>
    </ul>

    <H2>3. What Users Can Report</H2>
    <P>Where relevant features are available, reports may also concern unsafe AI responses, voice interactions, generated audio, lyrics, or artwork. A report about AI content is assessed for the safety and rights risks it creates, not treated as harmless merely because it was generated automatically.</P>
    <ul>
      <Li>Bullying, harassment, threats, hate, humiliation, or coordinated attacks.</Li>
      <Li>Grooming, sexualized communication, exploitation, requests for private images, or external-contact pressure.</Li>
      <Li>Private information sharing, exact location sharing, school identification, phone numbers, passwords, or unsafe external handles.</Li>
      <Li>Self-harm concerns, dangerous challenges, violent threats, illegal content, or other high-risk safety concerns.</Li>
      <Li>Fake accounts, impersonation, age deception, account evasion, spam, scams, phishing, or suspicious behavior.</Li>
      <Li>Content that is not age-appropriate for children aged 9–14.</Li>
    </ul>

    <H2>4. Priority Levels</H2>
    <P>Reports are triaged by risk. The most serious child safety cases are reviewed first.</P>
    <H3>Priority 1 — Emergency child safety risk</H3>
    <P>Examples: suspected child sexual abuse or exploitation, grooming, sextortion, immediate threat of violence, credible self-harm risk, offline meeting pressure, or a child in apparent imminent danger. These cases may trigger immediate restriction, evidence preservation, parent escalation, and authority reporting where required or appropriate.</P>

    <H3>Priority 2 — Serious abuse or repeated harm</H3>
    <P>Examples: bullying campaigns, harassment patterns, repeated private information requests, threatening behavior, evasion after restriction, impersonation, or repeated unsafe contact attempts. These cases may require human review and account-level enforcement.</P>

    <H3>Priority 3 — Content and community rule violations</H3>
    <P>Examples: inappropriate jokes, mild insults, unsafe creative content, spam, misleading content, or first-time lower-risk violations. These cases may result in content removal, warnings, RONI guidance, or cooldowns.</P>

    <H2>5. Review Process</H2>
    <ul>
      <Li>The report is received and assigned a category and risk level.</Li>
      <Li>Automated signals, RONI safety indicators, and available context may be reviewed.</Li>
      <Li>For serious cases, a human reviewer may evaluate the reported content, nearby context, account history, prior reports, and safety impact.</Li>
      <Li>Where needed, the reported user’s content or account features may be temporarily restricted while the case is reviewed.</Li>
      <Li>A decision is made based on platform rules, child-safety risk, available evidence, and applicable law.</Li>
      <Li>Appropriate action is taken and recorded in the safety log.</Li>
    </ul>

    <H2>6. Target Review Timing</H2>
    <P>ROMIND KIDS aims to review reports promptly. Review times may vary based on severity, volume, evidence, technical availability, and legal obligations.</P>
    <ul>
      <Li>Priority 1 reports: reviewed as quickly as reasonably possible, with immediate restrictions available for severe risks.</Li>
      <Li>Priority 2 reports: targeted review within 24–48 hours where operationally possible.</Li>
      <Li>Priority 3 reports: targeted review within 72 hours where operationally possible.</Li>
      <Li>Complex cases, appeals, cross-account patterns, or legal requests may take longer.</Li>
    </ul>

    <H2>7. Possible Outcomes</H2>
    <ul>
      <Li>No violation found, but safety guidance may still be provided.</Li>
      <Li>RONI warning, educational prompt, or safer-language suggestion.</Li>
      <Li>Content removal, message block, profile edit request, or visibility restriction.</Li>
      <Li>Temporary cooldown, messaging limitation, club/challenge restriction, or feature lock.</Li>
      <Li>Parent or guardian notification or parent action requirement.</Li>
      <Li>Temporary suspension or permanent account removal.</Li>
      <Li>Escalation to law enforcement, child protection organizations, or competent authorities where required or appropriate.</Li>
    </ul>

    <H2>8. Notifications</H2>
    <P>ROMIND KIDS may notify the reporting user, reported user, parent, or guardian about a report outcome where appropriate. Some details may be limited to protect child privacy, prevent retaliation, avoid revealing safety detection methods, or comply with legal obligations.</P>

    <H2>9. Appeals</H2>
    <P>A parent or legal guardian may request review of certain enforcement decisions by contacting <Value><a href={`mailto:${LEGAL.safetyEmail}`} className="text-cyan-300 hover:text-cyan-200 transition-colors">{LEGAL.safetyEmail}</a></Value>.</P>
    <ul>
      <Li>Appeals should include the child account identifier, report or enforcement notice if available, and the reason for the appeal.</Li>
      <Li>Appeals may be denied where the action was necessary for child safety, legal compliance, repeat abuse prevention, or protection of another child.</Li>
      <Li>We may not disclose another child’s private information as part of an appeal.</Li>
    </ul>

    <H2>10. Evidence Preservation</H2>
    <P>Evidence involving AI or voice features may include a transcription, prompt, response, generated-media identifier, or relevant moderation signal. Raw voice recordings should not be collected or preserved beyond what is necessary and lawful for the specific investigation. Access to such material must be restricted to authorized personnel or service providers.</P>
    <P>When a report involves serious child safety risk, exploitation, grooming, threats, or legal obligations, ROMIND KIDS may preserve relevant content, logs, account records, and moderation notes for investigation and reporting. This preservation may continue even if a user requests deletion, where legally permitted or required.</P>

    <H2>11. False or Malicious Reports</H2>
    <P>Reports should be made in good faith. False reporting, coordinated reporting, revenge reporting, or using reports to bully another child may itself violate the Community Guidelines and may lead to enforcement.</P>

    <H2>12. Contact</H2>
    <P>For safety reports, report-handling questions, urgent moderation concerns, or appeal requests, contact: <Value><a href={`mailto:${LEGAL.safetyEmail}`} className="text-cyan-300 hover:text-cyan-200 transition-colors">{LEGAL.safetyEmail}</a></Value></P>
  </LegalLayout>
);

export default ReportHandling;
